import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { plansService, featuresService, tenantSubscriptionsService, planMetadataService } from '../services/subscriptions';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ─── Plans Tab ───────────────────────────────────────────────────────────────

const DEFAULT_METADATA_FORM = {
  tier: 'basic',
  priceEur: '',
  unlockedFeatures: [],
  supportTier: 'none',
  freeCreditsIncluded: '',
};

const TIER_COLORS = { basic: 'default', pro: 'primary', enterprise: 'secondary' };

function PlansTab({ showSnackbar, env }) {
  const [plans, setPlans] = useState([]);
  const [metadataByPlan, setMetadataByPlan] = useState({});
  const [options, setOptions] = useState({ tiers: [], supportTiers: [], unlockedFeatures: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({
    planId: '',
    name: '',
    totalCredits: '',
    overspendEnabled: false,
    overspendLimit: '',
    ...DEFAULT_METADATA_FORM,
  });
  const [saving, setSaving] = useState(false);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [plansData, metaData, opts] = await Promise.all([
        plansService.getAll(env),
        planMetadataService.getAll(env).catch(() => []),
        planMetadataService.getOptions(env).catch(() => ({ tiers: [], supportTiers: [], unlockedFeatures: [] })),
      ]);
      const rawPlans = Array.isArray(plansData) ? plansData : plansData.plans || [];
      const rawMeta = Array.isArray(metaData) ? metaData : metaData.items || [];
      const metaMap = rawMeta.reduce((acc, m) => { acc[m.planId] = m; return acc; }, {});
      setPlans(rawPlans);
      setMetadataByPlan(metaMap);
      setOptions(opts);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleOpen = (plan = null) => {
    if (plan) {
      const planId = plan.planId || plan.id;
      const meta = metadataByPlan[planId] || {};
      setSelectedPlan(plan);
      setForm({
        planId: planId || '',
        name: plan.name || '',
        totalCredits: plan.totalCredits ?? '',
        overspendEnabled: plan.overspendEnabled ?? false,
        overspendLimit: plan.overspendLimit ?? '',
        tier: meta.tier ?? 'basic',
        priceEur: meta.priceEur ?? '',
        unlockedFeatures: meta.unlockedFeatures ?? [],
        supportTier: meta.supportTier ?? 'none',
        freeCreditsIncluded: meta.freeCreditsIncluded ?? '',
      });
    } else {
      setSelectedPlan(null);
      setForm({
        planId: '',
        name: '',
        totalCredits: '',
        overspendEnabled: false,
        overspendLimit: '',
        ...DEFAULT_METADATA_FORM,
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Credit values are always integers — the package's JS arithmetic in
      // changePlan / consumeCredits drifts on fractions (e.g. 0.2-0.1 ≠ 0.1).
      const planPayload = {
        planId: form.planId,
        name: form.name,
        totalCredits: Math.max(0, Math.round(Number(form.totalCredits) || 0)),
        overspendEnabled: form.overspendEnabled,
        overspendLimit: form.overspendLimit !== ''
          ? Math.max(0, Math.round(Number(form.overspendLimit)))
          : undefined,
      };
      const metadataPayload = {
        tier: form.tier,
        priceEur: form.priceEur === '' ? null : Number(form.priceEur),
        unlockedFeatures: form.unlockedFeatures,
        supportTier: form.supportTier,
        freeCreditsIncluded: form.freeCreditsIncluded === ''
          ? 0
          : Math.max(0, Math.round(Number(form.freeCreditsIncluded))),
      };

      if (selectedPlan) {
        const { planId: _id, ...updateData } = planPayload;
        await plansService.update(selectedPlan.planId || selectedPlan.id, updateData, env);
      } else {
        await plansService.create(planPayload, env);
      }
      await planMetadataService.upsert(form.planId, metadataPayload, env);
      showSnackbar(selectedPlan ? 'Plan updated successfully' : 'Plan created successfully');
      setDialogOpen(false);
      fetchPlans();
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || 'Failed to save plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await plansService.delete(selectedPlan.planId || selectedPlan.id, env);
      showSnackbar('Plan deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || 'Failed to delete plan', 'error');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Plans</Typography>
        <Box>
          <IconButton onClick={fetchPlans} sx={{ mr: 1 }}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>New Plan</Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Plan ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Total Credits</TableCell>
              <TableCell>Unlocked</TableCell>
              <TableCell>Overspend</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No plans found.</Typography>
                </TableCell>
              </TableRow>
            ) : plans.map((plan) => {
              const planId = plan.planId || plan.id;
              const meta = metadataByPlan[planId] || {};
              return (
                <TableRow key={planId} hover>
                  <TableCell><Typography variant="body2" fontFamily="monospace">{planId}</Typography></TableCell>
                  <TableCell>{plan.name}</TableCell>
                  <TableCell>
                    {meta.tier ? (
                      <Chip label={meta.tier} size="small" color={TIER_COLORS[meta.tier] || 'default'} />
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">{meta.priceEur != null ? `${meta.priceEur}€` : '—'}</TableCell>
                  <TableCell align="right">{plan.totalCredits}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {(meta.unlockedFeatures || []).length} features
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={plan.overspendEnabled ? `Yes (limit: ${plan.overspendLimit ?? '∞'})` : 'No'}
                      size="small"
                      color={plan.overspendEnabled ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(plan)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => { setSelectedPlan(plan); setDeleteDialogOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="overline" color="text.secondary">Identity</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Plan ID"
                value={form.planId}
                onChange={(e) => setForm({ ...form, planId: e.target.value })}
                disabled={!!selectedPlan}
                fullWidth
                required
              />
              <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
            </Box>

            <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Billing & Credits</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Tier</InputLabel>
                <Select
                  label="Tier"
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                >
                  {(options.tiers || []).map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Price (€/month)"
                type="number"
                value={form.priceEur}
                onChange={(e) => setForm({ ...form, priceEur: e.target.value })}
                placeholder="empty = custom/enterprise"
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Total Credits"
                type="number"
                inputProps={{ step: 1, min: 0 }}
                helperText="Integer only"
                value={form.totalCredits}
                onChange={(e) => setForm({ ...form, totalCredits: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Free Credits Included"
                type="number"
                inputProps={{ step: 1, min: 0 }}
                helperText="Integer only"
                value={form.freeCreditsIncluded}
                onChange={(e) => setForm({ ...form, freeCreditsIncluded: e.target.value })}
                fullWidth
              />
            </Box>
            <FormControlLabel
              control={<Switch checked={form.overspendEnabled} onChange={(e) => setForm({ ...form, overspendEnabled: e.target.checked })} />}
              label="Overspend Enabled"
            />
            {form.overspendEnabled && (
              <TextField label="Overspend Limit" type="number" inputProps={{ step: 1, min: 0 }} value={form.overspendLimit} onChange={(e) => setForm({ ...form, overspendLimit: e.target.value })} fullWidth />
            )}

            <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Entitlements</Typography>
            <Autocomplete
              multiple
              options={options.unlockedFeatures || []}
              value={form.unlockedFeatures}
              onChange={(_, val) => setForm({ ...form, unlockedFeatures: val })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Unlocked Features" placeholder="Select features" />}
            />
            <FormControl fullWidth>
              <InputLabel>Support Tier</InputLabel>
              <Select
                label="Support Tier"
                value={form.supportTier}
                onChange={(e) => setForm({ ...form, supportTier: e.target.value })}
              >
                {(options.supportTiers || []).map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.planId || !form.name}>
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete plan <strong>{selectedPlan?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Features Tab ────────────────────────────────────────────────────────────

function FeaturesTab({ showSnackbar, env }) {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ featureId: '', name: '', creditCost: '' });
  const [saving, setSaving] = useState(false);

  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await featuresService.getAll(env);
      setFeatures(Array.isArray(data) ? data : data.features || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await featuresService.create({
        featureId: form.featureId,
        name: form.name,
        creditCost: Math.max(0, Math.round(Number(form.creditCost) || 0)),
      }, env);
      showSnackbar('Feature created successfully');
      setDialogOpen(false);
      setForm({ featureId: '', name: '', creditCost: '' });
      fetchFeatures();
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || 'Failed to create feature', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Features</Typography>
        <Box>
          <IconButton onClick={fetchFeatures} sx={{ mr: 1 }}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>New Feature</Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Feature ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right">Credit Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No features found.</Typography>
                </TableCell>
              </TableRow>
            ) : features.map((f) => (
              <TableRow key={f.featureId || f.id} hover>
                <TableCell><Typography variant="body2" fontFamily="monospace">{f.featureId || f.id}</Typography></TableCell>
                <TableCell>{f.name}</TableCell>
                <TableCell align="right">{f.creditCost}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Feature</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Feature ID" value={form.featureId} onChange={(e) => setForm({ ...form, featureId: e.target.value })} fullWidth required />
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required />
            <TextField label="Credit Cost" type="number" inputProps={{ step: 1, min: 0 }} helperText="Integer only" value={form.creditCost} onChange={(e) => setForm({ ...form, creditCost: e.target.value })} fullWidth required />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.featureId || !form.name || !form.creditCost}>
            {saving ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Tenant Subscriptions Tab ────────────────────────────────────────────────

function TenantSubscriptionsTab({ showSnackbar, tenants, env }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [remaining, setRemaining] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [createPlanId, setCreatePlanId] = useState('');
  const [changePlanId, setChangePlanId] = useState('');
  const [changeStrategy, setChangeStrategy] = useState('immediate');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    plansService.getAll(env).then((data) => setPlans(Array.isArray(data) ? data : data.plans || [])).catch(() => {});
  }, [env]);

  const tenantId = selectedTenant?.tenant || selectedTenant?.id || null;

  const fetchTenantData = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoadingData(true);
      setError('');
      const [rem, sub, usg, cyc] = await Promise.allSettled([
        tenantSubscriptionsService.getRemaining(tenantId, env),
        tenantSubscriptionsService.getSubscription(tenantId, env),
        tenantSubscriptionsService.getUsage(tenantId, env),
        tenantSubscriptionsService.getCycles(tenantId, env),
      ]);
      setRemaining(rem.status === 'fulfilled' ? rem.value : null);
      setSubscription(sub.status === 'fulfilled' ? sub.value : null);
      // /usage returns UsageSummary { totalRunCount, totalCreditsConsumed, features: [...] }
      // /cycles returns CycleHistory[] directly.
      setUsage(
        usg.status === 'fulfilled'
          ? (Array.isArray(usg.value) ? usg.value : usg.value.features || [])
          : []
      );
      setCycles(
        cyc.status === 'fulfilled'
          ? (Array.isArray(cyc.value) ? cyc.value : cyc.value.cycles || [])
          : []
      );
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load tenant data');
    } finally {
      setLoadingData(false);
    }
  }, [tenantId, env]);

  useEffect(() => { fetchTenantData(); }, [fetchTenantData]);

  const handleCreateSubscription = async () => {
    try {
      setSaving(true);
      await tenantSubscriptionsService.create(tenantId, { planId: createPlanId }, env);
      showSnackbar('Subscription created successfully');
      setCreateDialogOpen(false);
      setCreatePlanId('');
      fetchTenantData();
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || 'Failed to create subscription', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePlan = async () => {
    try {
      setSaving(true);
      await tenantSubscriptionsService.changePlan(tenantId, { newPlanId: changePlanId, strategy: changeStrategy }, env);
      showSnackbar('Plan changed successfully');
      setChangePlanDialogOpen(false);
      fetchTenantData();
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || 'Failed to change plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOverspend = async (enabled) => {
    try {
      await tenantSubscriptionsService.toggleOverspend(tenantId, enabled, env);
      showSnackbar(`Overspend ${enabled ? 'enabled' : 'disabled'}`);
      fetchTenantData();
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || 'Failed to toggle overspend', 'error');
    }
  };

  const handleCancel = async () => {
    try {
      await tenantSubscriptionsService.cancel(tenantId, env);
      showSnackbar('Subscription cancelled');
      setCancelDialogOpen(false);
      fetchTenantData();
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || 'Failed to cancel subscription', 'error');
    }
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Tenant Subscriptions</Typography>
        <Autocomplete
          options={tenants}
          getOptionLabel={(t) => `${t.name} (${t.tenant || t.id})`}
          value={selectedTenant}
          onChange={(_, val) => setSelectedTenant(val)}
          renderInput={(params) => <TextField {...params} label="Select Tenant" placeholder="Search tenants..." />}
          sx={{ maxWidth: 500 }}
          isOptionEqualToValue={(opt, val) => (opt.tenant || opt.id) === (val.tenant || val.id)}
        />
      </Box>

      {!tenantId && (
        <Alert severity="info">Select a tenant above to manage their subscription.</Alert>
      )}

      {tenantId && loadingData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      )}

      {tenantId && !loadingData && error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {tenantId && !loadingData && !error && (
        <>
          {/* Actions Bar */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            <Button variant="contained" size="small" onClick={() => setCreateDialogOpen(true)}>Create Subscription</Button>
            <Button variant="outlined" size="small" onClick={() => setChangePlanDialogOpen(true)}>Change Plan</Button>
            <Button variant="outlined" size="small" color="error" onClick={() => setCancelDialogOpen(true)}>Cancel Subscription</Button>
            <IconButton onClick={fetchTenantData}><RefreshIcon /></IconButton>
          </Box>

          {/* Credits Remaining */}
          {remaining && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Credits Remaining</Typography>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" color="primary">{remaining.remaining ?? remaining.credits ?? '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {remaining.total != null ? `of ${remaining.total} total` : 'remaining credits'}
                  </Typography>
                </Box>
                {remaining.planId && (
                  <Chip label={`Plan: ${remaining.planId}`} />
                )}
                {remaining.overspendEnabled != null && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={remaining.overspendEnabled}
                        onChange={(e) => handleToggleOverspend(e.target.checked)}
                      />
                    }
                    label="Overspend"
                  />
                )}
              </Box>
            </Paper>
          )}

          {/* Usage Breakdown */}
          <Accordion defaultExpanded={usage.length > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold">Usage by Feature</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {usage.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No usage data available.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Feature</TableCell>
                        {!isMobile && <TableCell>Feature ID</TableCell>}
                        <TableCell align="right">Credits Used</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usage.map((u, i) => (
                        <TableRow key={u.featureId || i}>
                          <TableCell>{u.name || u.featureId}</TableCell>
                          {!isMobile && <TableCell><Typography variant="body2" fontFamily="monospace">{u.featureId}</Typography></TableCell>}
                          <TableCell align="right">{u.creditsConsumed ?? '—'}</TableCell>
                          <TableCell align="right">{u.runCount ?? '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Cycle History */}
          {(() => {
            const currentCycle = subscription && subscription.cycleStartedAt
              ? {
                  cycleId: subscription.currentCycleId || 'current',
                  cycleStartedAt: subscription.cycleStartedAt,
                  cycleEndedAt: subscription.cycleEndsAt,
                  totalCredits: subscription.totalCredits,
                  creditsUsed: subscription.creditsUsed,
                  isCurrent: true,
                }
              : null;
            const cycleRows = currentCycle ? [currentCycle, ...cycles] : cycles;
            return (
              <Accordion sx={{ mt: 1 }} defaultExpanded={cycleRows.length > 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" fontWeight="bold">Cycle History</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {cycleRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No cycle history available.</Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Cycle</TableCell>
                            <TableCell>Start</TableCell>
                            <TableCell>End</TableCell>
                            <TableCell align="right">Credits Used</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="center">Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cycleRows.map((c, i) => (
                            <TableRow key={c.cycleId || i}>
                              <TableCell>{c.cycleId ?? i + 1}</TableCell>
                              <TableCell>{c.cycleStartedAt ? new Date(c.cycleStartedAt).toLocaleDateString() : '—'}</TableCell>
                              <TableCell>{c.cycleEndedAt ? new Date(c.cycleEndedAt).toLocaleDateString() : '—'}</TableCell>
                              <TableCell align="right">{c.creditsUsed ?? '—'}</TableCell>
                              <TableCell align="right">{c.totalCredits ?? '—'}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={c.isCurrent ? 'Active' : 'Completed'}
                                  size="small"
                                  color={c.isCurrent ? 'primary' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })()}
        </>
      )}

      {/* Create Subscription Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign a plan to <strong>{selectedTenant?.name}</strong>
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Plan</InputLabel>
            <Select value={createPlanId} onChange={(e) => setCreatePlanId(e.target.value)} label="Plan">
              {plans.map((p) => (
                <MenuItem key={p.planId || p.id} value={p.planId || p.id}>{p.name} ({p.planId || p.id})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSubscription} disabled={saving || !createPlanId}>
            {saving ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={changePlanDialogOpen} onClose={() => setChangePlanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Plan</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>New Plan</InputLabel>
              <Select value={changePlanId} onChange={(e) => setChangePlanId(e.target.value)} label="New Plan">
                {plans.map((p) => (
                  <MenuItem key={p.planId || p.id} value={p.planId || p.id}>{p.name} ({p.planId || p.id})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Strategy</InputLabel>
              <Select value={changeStrategy} onChange={(e) => setChangeStrategy(e.target.value)} label="Strategy">
                <MenuItem value="immediate">Immediate</MenuItem>
                <MenuItem value="next_cycle">Next Cycle</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePlanDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePlan} disabled={saving || !changePlanId}>
            {saving ? <CircularProgress size={24} /> : 'Change Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to cancel the subscription for <strong>{selectedTenant?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleCancel}>Cancel Subscription</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Main SubscriptionsPage ──────────────────────────────────────────────────

export default function SubscriptionsPage({ tenants, env = 'test' }) {
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Subscriptions
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Plans" />
          <Tab label="Features" />
          <Tab label="Tenant Subscriptions" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <PlansTab showSnackbar={showSnackbar} env={env} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <FeaturesTab showSnackbar={showSnackbar} env={env} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <TenantSubscriptionsTab showSnackbar={showSnackbar} tenants={tenants} env={env} />
      </TabPanel>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
